<template>
  <div
    class="calc-main ltv sliders"
    data-name="LTV calculator"
  >
    <div
      v-if="!isSubmitted"
      id="sliders-page"
    >
      <section class="calc-main__container">
        <form
          class="form_wrapper"
          action
        >
          <div class="row">
            <div class="col-12 col-md-6 input-fields">
              <div class="block-header">
                <h3>Existing mortgage information</h3>
              </div>
              <div
                id="input"
                class="input__wrapper"
              >
                <div
                  id="estimated-house-value-input-group"
                  class="form-group"
                >
                  <label
                    for="estimated-house-value-input-field"
                  >Estimated Home Value
                    <span
                      class="tooltipContent"
                      data-toggle="tooltip"
                      data-placement="right"
                      data-html="true"
                      title="<p>If you are unsure of the current appraised value of your home, consider what similar homes in your area are selling for, which may be helpful in determining current value.</p>"
                    >
                      <i class="fa fa-info-circle fa-1x" />
                    </span>
                  </label>
                  <div class="input-group mb-2">
                    <div class="input-group-prepend">
                      <div class="input-group-text">$</div>
                    </div>
                    <CurrencyComponent
                      v-model.number="$v.estHomeValue.$model"
                      class="form-control"
                      aria-label="Estimated Home Value"
                      :class="{
                        'is-invalid':
                          $v.estHomeValue.$error ||
                          $v.estHomeValue.$invalid
                      }"
                      @input="updateResults"
                    />
                    <div class="input-group-append">
                      <div class="input-group-text">&nbsp;</div>
                    </div>
                    <input
                      id="estimated-house-value-input-range"
                      v-model.number="estHomeValue"
                      type="range"
                      aria-label="Estimated Home Value Range"
                      class="custom-range"
                      min="10000"
                      max="5000000"
                      @input="updateResults"
                      @change="sendRangeToDataLayer()"
                    >
                    <div class="invalid-feedback">
                      <span
                        v-if="
                          !$v.estHomeValue.between ||
                            !$v.estHomeValue.required
                        "
                      >{{ errorMsgPre }} ${{
                        Number(
                          $v.estHomeValue.$params.between.min
                        ).toLocaleString()
                      }}
                        and ${{
                          Number(
                            $v.estHomeValue.$params.between.max
                          ).toLocaleString()
                        }}</span>
                    </div>
                  </div>
                </div>
                <div
                  id="first-mortgage-balance-input-group"
                  class="form-group"
                >
                  <label
                    for="first-mortgage-balance-input-field"
                  >First Mortgage Balance
                    <span
                      class="tooltipContent"
                      data-toggle="tooltip"
                      data-placement="right"
                      data-html="true"
                      title="<p>This is the current principal balance or payoff amount.</p>"
                    >
                      <i class="fa fa-info-circle fa-1x" />
                    </span>
                  </label>
                  <div class="input-group mb-2">
                    <div class="input-group-prepend">
                      <div class="input-group-text">$</div>
                    </div>
                    <CurrencyComponent
                      v-model.number="$v.firstMortgageBalance.$model"
                      class="form-control"
                      aria-label="First Mortgage Balance"
                      :class="{
                        'is-invalid':
                          $v.firstMortgageBalance.$error ||
                          $v.firstMortgageBalance.$invalid
                      }"
                      @input="updateResults"
                    />
                    <div class="input-group-append">
                      <div class="input-group-text">&nbsp;</div>
                    </div>
                    <input
                      id="first-mortgage-balance-range"
                      v-model.number="firstMortgageBalance"
                      type="range"
                      aria-label="First Mortgage Balance Range"
                      class="custom-range"
                      min="1000"
                      max="5000000"
                      @input="updateResults"
                      @change="sendRangeToDataLayer()"
                    >
                    <div class="invalid-feedback">
                      <span
                        v-if="
                          !$v.firstMortgageBalance.between ||
                            !$v.firstMortgageBalance.required
                        "
                      >{{ errorMsgPre }} ${{
                        Number(
                          $v.firstMortgageBalance.$params.between.min
                        ).toLocaleString()
                      }}
                        and ${{
                          Number(
                            $v.firstMortgageBalance.$params.between.max
                          ).toLocaleString()
                        }}</span>
                    </div>
                  </div>
                </div>
                <div
                  id="second-mortgage-balance-input-group"
                  class="form-group"
                >
                  <label
                    for="second-mortgage-balance-input-field"
                  >Second Mortgage Balance (If Applicable)
                    <span
                      class="tooltipContent"
                      data-toggle="tooltip"
                      data-placement="right"
                      data-html="true"
                      title="<p>This is your payoff amount or current principal balance on your second mortgage.</p>"
                    >
                      <i class="fa fa-info-circle fa-1x" />
                    </span>
                  </label>
                  <div class="input-group mb-2">
                    <div class="input-group-prepend">
                      <div class="input-group-text">$</div>
                    </div>
                    <CurrencyComponent
                      v-model.number="$v.secondMortgageBalance.$model"
                      class="form-control"
                      aria-label="Second Mortgage Balance"
                      :class="{
                        'is-invalid':
                          $v.secondMortgageBalance.$error ||
                          $v.secondMortgageBalance.$invalid
                      }"
                      @input="updateResults"
                    />
                    <div class="input-group-append">
                      <div class="input-group-text">&nbsp;</div>
                    </div>
                    <input
                      id="second-mortgage-balance-input-range"
                      v-model.number="secondMortgageBalance"
                      type="range"
                      aria-label="Second Mortgage Balance Range"
                      class="custom-range"
                      min="1000"
                      max="4000000"
                      @input="updateResults"
                      @change="sendRangeToDataLayer()"
                    >
                    <div class="invalid-feedback">
                      <span
                        v-if="
                          !$v.secondMortgageBalance.between ||
                            !$v.secondMortgageBalance.required
                        "
                      >{{ errorMsgPre }} ${{
                        Number(
                          $v.secondMortgageBalance.$params.between.min
                        ).toLocaleString()
                      }}
                        and ${{
                          Number(
                            $v.secondMortgageBalance.$params.between.max
                          ).toLocaleString()
                        }}</span>
                    </div>
                  </div>
                </div>
                <div
                  id="third-mortgage-balance-input-group"
                  class="form-group"
                >
                  <label
                    for="third-mortgage-balance-input-field"
                  >Third Mortgage Balance or Any Other Liens on Your Home (If Applicable)
                    <span
                      class="tooltipContent"
                      data-toggle="tooltip"
                      data-placement="right"
                      data-html="true"
                      title="<p>This is your payoff amount or current principal balance on your third mortgage and all other lines on your property.</p>"
                    >
                      <i class="fa fa-info-circle fa-1x" />
                    </span>
                  </label>
                  <div class="input-group mb-2">
                    <div class="input-group-prepend">
                      <div class="input-group-text">$</div>
                    </div>
                    <CurrencyComponent
                      v-model.number="$v.thirdMortgageBalance.$model"
                      class="form-control"
                      aria-label="Third Mortgage Balance or Any Other Liens On Your Home"
                      :class="{
                        'is-invalid':
                          $v.thirdMortgageBalance.$error ||
                          $v.thirdMortgageBalance.$invalid
                      }"
                      @input="updateResults"
                    />
                    <div class="input-group-append">
                      <div class="input-group-text">&nbsp;</div>
                    </div>
                    <input
                      id="first-mortgage-balance-input-range"
                      v-model.number="thirdMortgageBalance"
                      type="range"
                      aria-label="Third Mortgage Balance or Any Other Liens On Your Home Range"
                      class="custom-range"
                      min="1000"
                      max="3500000"
                      @input="updateResults"
                      @change="sendRangeToDataLayer()"
                    >
                    <div class="invalid-feedback">
                      <span
                        v-if="
                          !$v.thirdMortgageBalance.between ||
                            !$v.thirdMortgageBalance.required
                        "
                      >{{ errorMsgPre }} ${{
                        Number(
                          $v.thirdMortgageBalance.$params.between.min
                        ).toLocaleString()
                      }}
                        and ${{
                          Number(
                            $v.thirdMortgageBalance.$params.between.max
                          ).toLocaleString()
                        }}</span>
                    </div>
                  </div>
                </div>

              </div>
              <div
                id="button-section"
                class="col"
              >

                <div class="row">
                  <div
                    id="form-mod-group"
                    class="form-group col col-md-6"
                  >
                    <span
                      id="refi-calc-reset-btn"
                      class="text-button button-white fm-btn"
                      onclick="dataLayer.push({
                          'event': 'calculator-cta-link',
                          'calculatorName': 'LTV calculator',
                          'calculatorCta': 'Reset',
                        });"
                      @click="reset"
                    >
                      Reset
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div
              id="result"
              class="col-12 col-md-6 chart"
            >
              <div class="sticky">
                <div
                  id="graph-info"
                  class="tab-pane fade show active"
                  role="tabpanel"
                  aria-labelledby="graph-info"
                >
                  <div class="card-main graph">
                    <div class="card-bottom">
                      <div class="row graph-content">
                        <div v-if="isOneValue">
                          <CircleComponent
                            :values="graphDataValues"
                            :center-text="graphDataCenterText"
                            :value-type="'percentage'"
                          />
                        </div>
                        <div v-else>
                          <GraphComponent
                            :values="graphDataValues"
                            :center-text="graphDataCenterText"
                            :value-type="'percentage'"
                          />
                        </div>
                      </div>
                      <div class="row graph-text">
                        <label class="col-8">First mortgage LTV ratio<span>&#42;</span></label>
                        <span
                          class="col-4 magenta"
                          style="font-size: 24px; font-weight: 700"
                        > {{ firstMortgageLTVRatio }}%</span>
                      </div>
                      <div
                        v-if="secondMortgageBalance"
                        class="row graph-text"
                      >
                        <label class="col-8">Second mortgage LTV ratio </label>
                        <span
                          class="col-4 blue-light"
                          style="font-size: 24px; font-weight: 700;"
                        >{{ secondMortgageLTVRatio }}%</span>
                      </div>
                      <div
                        v-if="thirdMortgageBalance"
                        class="row graph-text"
                      >
                        <label class="col-8">Third mortgage LTV ratio </label>
                        <span
                          class="col-4 navy"
                          style="font-size: 24px; font-weight: 700;"
                        >{{ thirdMortgageLTVRatio }}%</span>
                      </div>
                      <div class="row graph-last-text">
                        <label class="col-8">Total loan-to-value ratio </label>
                        <span
                          class="col-4"
                          style="color: #212121; font-size: 24px; font-weight: 700; text-align: right"
                        >{{ totalLoanToValueRatio }}%</span>
                      </div>
                      <div
                        class="row graph-last-text"
                        style="padding-left: 10px;"
                      >
                        <span class="card-notes"><small><i>*This number may be used to determine your refinance and modification options.</i></small></span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </form>
      </section>
    </div>
  </div>
</template>


<script>
import jQuery from 'jquery';
import { required, between } from 'vuelidate/lib/validators';
import GraphComponent from '../components/GraphComponent.vue';
import CurrencyComponent from '../components/Currency.vue';
import CircleComponent from '../components/CircleComponent.vue';

const $ = jQuery;
const currency = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 0,
  minimumFractionDigits: 0,
  style: 'currency',
  currency: 'USD',
});

export default {
  name: 'LTVCalculator',
  components: {
    CircleComponent,
    GraphComponent,
    CurrencyComponent,
  },
  data() {
    return this.init();
  },
  validations: {
    estHomeValue: {
      required,
      between: between(10000, 5000000),
    },
    firstMortgageBalance: {
      required,
      between: between(1000, 5000000),
    },
    secondMortgageBalance: {
      between: between(0, 4000000),
    },
    thirdMortgageBalance: {
      between: between(0, 3500000),
    },
  },
  computed: {
    graphDataValues() {
      if (
        this.secondMortgageLTVRatio === 0 &&
        this.thirdMortgageLTVRatio === 0
      ) {
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.isOneValue = true;
      } else {
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.isOneValue = false;
      }
      const valOne = Object();
      valOne.value = this.firstMortgageLTVRatio;
      valOne.color = '#911A58';

      const valTwo = Object();
      valTwo.value = this.secondMortgageLTVRatio;
      valTwo.color = '#0C77BA';

      const valThree = Object();
      valThree.value = this.thirdMortgageLTVRatio;
      valThree.color = '#05314D';

      // eslint-disable-next-line
      const values = Array();
      values[0] = valOne;
      values[1] = valTwo;
      values[2] = valThree;
      return values;
    },
    graphDataCenterText() {
      // eslint-disable-next-line
      const centerText = Array();
      centerText[0] = 'Total loan-to-';
      centerText[1] = 'value ratio';
      centerText[2] = this.totalLoanToValueRatio;

      return centerText;
    },
  },
  mounted() {
    $('.sliders').tooltip({ selector: '[data-toggle="tooltip"]' });
    // eslint-disable-next-line func-names
    $(document).on('touchstart', '.tooltipContent', function() {
      $('.tooltip').each(() => {
        $('.tooltip').tooltip('hide');
      });
      $(this).tooltip('show');
    });
    // eslint-disable-next-line func-names
    $(document).on('touchend', '.tooltip', function() {
      $(this).tooltip('hide');
    });

    // eslint-disable-next-line no-unused-vars
    $(document).on('touchstart', 'body', event => {
      if (
        event.target.id !== '' &&
        (!event.target.id.indexOf('input-range') >= 0 ||
          !event.target.id.indexOf('input') >= 0 ||
          !event.target.id.indexOf('input-group') >= 0 ||
          !event.target.id.indexOf('input-select') >= 0)
      ) {
        $('.tooltip').tooltip('hide');
      }
    });
  },
  methods: {
    init() {
      return {
        isOneValue: false,
        isSubmitted: false,
        estHomeValue: 200000,
        firstMortgageBalance: 150000,
        secondMortgageBalance: 0,
        thirdMortgageBalance: 0,
        totalAmountOwed: 150000,
        firstMortgageLTVRatio: 75,
        secondMortgageLTVRatio: 0,
        thirdMortgageLTVRatio: 0,
        totalLoanToValueRatio: 75,
        errorMsgPre: 'Please enter a valid amount between',
      };
    },
    reset() {
      this.estHomeValue = this.init().estHomeValue;
      this.firstMortgageBalance = this.init().firstMortgageBalance;
      this.secondMortgageBalance = this.init().secondMortgageBalance;
      this.thirdMortgageBalance = this.init().thirdMortgageBalance;
      this.totalAmountOwed = this.init().totalAmountOwed;
      this.firstMortgageLTVRatio = this.init().firstMortgageLTVRatio;
      this.secondMortgageLTVRatio = this.init().secondMortgageLTVRatio;
      this.thirdMortgageLTVRatio = this.init().thirdMortgageLTVRatio;
      this.totalLoanToValueRatio = this.init().totalLoanToValueRatio;
      this.isOneValue = true;
    },
    handleNegativeNums(n) {
      if (n < 0) {
        return 0;
      }
      return n;
    },
    calculateRatio(x, y) {
      return parseFloat(((x / y) * 100).toFixed(2));
    },
    calculateSum(nums) {
      let sum = 0;
      nums.forEach(n => {
        sum += parseFloat(n);
      });
      return sum;
    },
    updateResults() {
      this.totalAmountOwed = this.calculateSum([
        this.firstMortgageBalance,
        this.secondMortgageBalance,
        this.thirdMortgageBalance,
      ]);
      this.firstMortgageLTVRatio = this.calculateRatio(
        this.firstMortgageBalance,
        this.estHomeValue
      );
      this.secondMortgageLTVRatio = this.calculateRatio(
        this.secondMortgageBalance,
        this.estHomeValue
      );
      this.thirdMortgageLTVRatio = this.calculateRatio(
        this.thirdMortgageBalance,
        this.estHomeValue
      );
      this.totalLoanToValueRatio = this.calculateRatio(
        this.totalAmountOwed,
        this.estHomeValue
      );
    },
    currencyFormat(n) {
      return currency.format(n);
    },
    formatNumber(num) {
      return Number(num).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    sendRangeToDataLayer() {
      window.dataLayer.push({
        event: 'calc-interaction',
        calculatorName: 'LTV calculator',
      });
    },
  },
};
</script>
<style lang="sass">
</style>
